import { useRef, useState } from 'react'
import { Input, Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react'
import { ChromePicker } from 'react-color'
import { useQueryClient } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import { usePortalContext } from '@/pages/portal/Portal.page.tsx'
import TagEditDialog, { useTagEditDialog } from '@/components/portal/TagEditDialog.tsx'

const DEFAULT_TAG_COLOR = '#7EB6E6'

export function useTagManagementModal() {
  const modalRef = useRef<HTMLDialogElement>(null)
  return {
    modalRef,
    show: () => setTimeout(() => modalRef.current?.showModal(), 0),
    close: () => modalRef.current?.close(),
  }
}

interface TagManagementModalProps {
  modalRef: React.RefObject<HTMLDialogElement | null>
}

export function TagManagementModal({ modalRef }: TagManagementModalProps) {
  const { createTag, updateTag, deleteTag } = useClient()
  const queryClient = useQueryClient()
  const portalContext = usePortalContext()
  const tags = portalContext?.account?.tags ?? []

  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [showNewColorPicker, setShowNewColorPicker] = useState(false)

  const tagEdit = useTagEditDialog()

  async function handleEditSave(tagId: string, data: { name: string, color: string }) {
    await updateTag(tagId, data)
    invalidate()
  }

  async function handleDelete(id: string) {
    setIsSaving(true)
    try {
      await deleteTag(id)
      invalidate()
      setDeletingId(null)
    } finally {
      setIsSaving(false)
    }
  }

  async function handleCreate() {
    if (!newName.trim() || isSaving) return
    setIsSaving(true)
    try {
      await createTag({ name: newName.trim(), color: newColor || undefined })
      invalidate()
      setNewName('')
      setNewColor('')
      setShowNewColorPicker(false)
    } finally {
      setIsSaving(false)
    }
  }

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
    queryClient.invalidateQueries({ queryKey: ['projects'] })
  }

  return (
     // @ts-ignore ref type mismatch with DaisyUI Modal
    <Modal ref={modalRef} className="bg-neutral max-w-md">
      <Modal.Header>
        <span className="text-xl font-bold">Tags verwalten</span>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-1">
          {tags.map(tag => (
            <div key={tag.id}>
              {deletingId === tag.id ? (
                <div className="flex items-center gap-2 p-2 bg-base-200 rounded-lg">
                  <span className="text-sm flex-1">„{tag.name}" wirklich löschen?</span>
                  <Button size="sm" color="error" onClick={() => handleDelete(tag.id)} disabled={isSaving}>Löschen</Button>
                  <Button size="sm" color="ghost" onClick={() => setDeletingId(null)}>Abbrechen</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-base-200">
                  <span
                    className="w-5 h-5 rounded-full shrink-0 cursor-pointer border border-base-300"
                    style={{ backgroundColor: tag.color ? `#${tag.color}` : DEFAULT_TAG_COLOR }}
                    onClick={() => tagEdit.open(tag)}
                    title="Tag bearbeiten"
                  />
                  <span className="flex-1 text-sm">{tag.name}</span>
                  <Button size="sm" color="ghost" className="px-2" onClick={() => tagEdit.open(tag)}>
                    <IconPencil size={15} />
                  </Button>
                  <Button size="sm" color="ghost" className="px-2" onClick={() => setDeletingId(tag.id)}>
                    <IconTrash size={15} />
                  </Button>
                </div>
              )}
            </div>
          ))}

          {tags.length === 0 && (
            <p className="text-sm opacity-60 py-2">Noch keine Tags vorhanden.</p>
          )}
        </div>

        <div className="divider my-2" />

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span
              className="w-7 h-7 rounded-full border-2 border-base-300 shrink-0 cursor-pointer"
              style={{ backgroundColor: newColor ? `#${newColor}` : DEFAULT_TAG_COLOR }}
              onClick={() => setShowNewColorPicker(prev => !prev)}
              title="Farbe wählen"
            />
            <Input
              size="sm"
              className="flex-1"
              placeholder="Neuen Tag erstellen..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              maxLength={64}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
            />
            <Button size="sm" color="primary" className="px-2" onClick={handleCreate} disabled={isSaving || !newName.trim()}>
              <IconPlus size={16} />
            </Button>
          </div>
          {showNewColorPicker && (
            <div className="card bg-base-200">
              <div className="card-body p-3 items-center gap-3">
                <ChromePicker
                  disableAlpha
                  // @ts-ignore
                  color={newColor ? `#${newColor}` : DEFAULT_TAG_COLOR}
                  onChange={(c) => setNewColor(c.hex.replace('#', ''))}
                />
                <div className="flex gap-2">
                  {newColor && (
                    <Button size="sm" color="ghost" onClick={() => setNewColor('')}>
                      Farbe entfernen
                    </Button>
                  )}
                  <Button size="sm" color="ghost" onClick={() => setShowNewColorPicker(false)}>
                    Fertig
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <TagEditDialog
        modalRef={tagEdit.modalRef}
        tag={tagEdit.tag}
        onSave={handleEditSave}
      />
    </Modal>
  )
}
